import { useMutation, ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { CREATE_BULK_MESSAGE_MUTATION } from "@/api/mutations/createBulkMessage";
import {
  CreateBulkMessageMutation,
  CreateBulkMessageMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";
import { FormValues, SubmissionReturn } from "@/pages/BulkMessaging/types";
import { formToCreateApi } from "@/pages/BulkMessaging/utils/mutationBuilders";
import { studentParser } from "@/pages/BulkMessaging/utils/responseParsers";

interface CreateBulkMessageReturn {
  loading: boolean;
  error: ApolloError | undefined;
  data: SubmissionReturn;
}

interface CreateBulkMessageParams {
  formValues: FormValues;
  orgId: string | undefined;
  userId: string | undefined;
  draft: boolean;
}

export const useCreateBulkMessage = (): [
  (params: CreateBulkMessageParams) => void,
  CreateBulkMessageReturn,
] => {
  const [createBulkMessageMutation, { loading, data, error }] = useMutation<
    CreateBulkMessageMutation,
    CreateBulkMessageMutationVariables
  >(CREATE_BULK_MESSAGE_MUTATION);

  const createBulkMessage = useCallback(
    (params: CreateBulkMessageParams) => {
      const { orgId, userId, ...rest } = params;
      if (orgId && userId) {
        const variables = formToCreateApi({ orgId, userId, ...rest });
        createBulkMessageMutation({ variables });
      }
    },
    [createBulkMessageMutation],
  );

  const errorHandler =
    data && MutationErrorHandler.fromResult({ data }, error?.graphQLErrors);

  const bulkMessage = data?.createBulkMessageMutation?.bulkMessage;

  const returnData = {
    id: bulkMessage?.id,
    result: errorHandler?.toResult(),
    draft: bulkMessage?.draft,
    audienceReach: {
      students: studentParser(bulkMessage?.audienceReach),
      totalCount: bulkMessage?.audienceReach.totalCount || 0,
    },
  };

  return [createBulkMessage, { loading, data: returnData, error }];
};
